import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type UnitSystem = "metric" | "imperial";

interface UnitsContextValue {
  system: UnitSystem;
  setSystem: (s: UnitSystem) => void;
}

const UnitsContext = createContext<UnitsContextValue | null>(null);
const STORAGE_KEY = "hudson.units";

function readStored(): UnitSystem {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "metric" || v === "imperial") return v;
  } catch {
    // ignore
  }
  return "metric";
}

export function UnitsProvider({ children }: { children: ReactNode }) {
  const [system, setSystemState] = useState<UnitSystem>(readStored);

  const setSystem = useCallback((s: UnitSystem) => {
    setSystemState(s);
    try {
      localStorage.setItem(STORAGE_KEY, s);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(() => ({ system, setSystem }), [system, setSystem]);
  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
}

export function useUnits(): UnitsContextValue {
  const ctx = useContext(UnitsContext);
  if (!ctx) throw new Error("useUnits() must be used within a UnitsProvider");
  return ctx;
}

const METERS_PER_MILE = 1609.344;
const METERS_PER_FOOT = 0.3048;

export function formatDistance(meters: number, system: UnitSystem): string {
  if (!Number.isFinite(meters)) return "–";
  if (system === "imperial") {
    const miles = meters / METERS_PER_MILE;
    if (miles >= 0.1) return `${miles.toFixed(2)} mi`;
    return `${(meters / METERS_PER_FOOT).toFixed(0)} ft`;
  }
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${meters.toFixed(0)} m`;
}

export function formatSpeed(metersPerSecond: number, system: UnitSystem): string {
  if (!Number.isFinite(metersPerSecond)) return "–";
  if (system === "imperial") return `${(metersPerSecond * 2.2369362921).toFixed(1)} mph`;
  return `${(metersPerSecond * 3.6).toFixed(1)} km/h`;
}

export function formatTemp(celsius: number, system: UnitSystem): string {
  if (!Number.isFinite(celsius)) return "–";
  if (system === "imperial") return `${((celsius * 9) / 5 + 32).toFixed(0)}°F`;
  return `${celsius.toFixed(0)}°C`;
}

export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "–";
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

export function formatEnergy(wattHours: number): string {
  if (!Number.isFinite(wattHours)) return "–";
  if (wattHours >= 1000) return `${(wattHours / 1000).toFixed(2)} kWh`;
  return `${wattHours.toFixed(1)} Wh`;
}

export function formatDateTime(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(d);
}

export function formatDate(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeZone: "UTC" }).format(d);
}
